// pms-core / domain — CANCELLATION TERMS as rules (PMS-PHONE-BOOKING-BRIEF P11–P15, §4.4).
//
// The free-text `pms_rate_plan.cancellation_policy` is replaced by named policies,
// one per rate plan, edited on their own settings page (Configuration › Conditions).
// Every reader — the call script, the read-back, the confirmation (FR / EN), the
// website and later the cancellation itself — renders or computes from these
// rules, so a phrase is never typed twice. Pure: no clock, no database.
//
//   Direct (root plans)      prepayment 30 % at booking · more than 5 days before
//                            arrival 30 % retained · then 50 % · no-show 100 %
//   OTA — Booking.com (-BK)  prepayment 50 % at booking · 50 % on any cancellation
//                            after reservation · no-show 100 %
//   OTA — Expedia (-EX)      as Booking.com, the prepayment taken at CHECK-IN (P14)
//
// « More than N days » is read strictly (§7 Q11): arrival Friday 4 December, N = 5
// → the first tier until Saturday 28 November inclusive (D−6), the next from
// Sunday 29 November (D−5). « Montant total » = room nights, board and extras,
// never the taxe de séjour (§7 Q10) — the caller passes that total.

import type { Cents, IsoDate } from './types.js';

export type PrepaymentAt = 'booking' | 'checkin';

/** One step of the scale. `moreThanDaysBefore: null` = until arrival — and, when
 *  it is the only tier, « at any time after reservation » (the OTA wording). */
export interface CancellationTier {
  moreThanDaysBefore: number | null;
  retainPct: number;
}

export interface CancellationPolicy {
  code: string;
  labelFr: string;
  /** Mirrors an extranet (P15): the page never pushes it, the operator confirms
   *  the extranet shows it (`verifiedAt`). */
  ota: boolean;
  prepaymentPct: number;
  prepaymentAt: PrepaymentAt;
  /** Ordered: decreasing thresholds, the last one `null`. */
  tiers: CancellationTier[];
  noShowPct: number;
  /** OTA policies only: when the operator last confirmed the extranet shows these
   *  terms. Cleared by the database when a rule changes. */
  verifiedAt: string | null;
}

/** The rules alone — what a stay copies at sale and the public offer carries:
 *  everything that decides an amount or a sentence, nothing about the page. */
export type TermsRules = Pick<CancellationPolicy, 'prepaymentPct' | 'prepaymentAt' | 'tiers' | 'noShowPct'>;

/** What a stay copies at sale (`pms_policy_terms`): the rules, and which
 *  policy they came from for the reader. `relaxed`: the desk's numbers for this
 *  guest rather than the policy's (PB4, `pms_relaxed_terms`). */
export type SoldTerms = TermsRules & { code?: string; labelFr?: string; relaxed?: boolean };

/** A policy as stored: the établissement's, with its identity. */
export interface StoredCancellationPolicy extends CancellationPolicy {
  id: number;
}

const isPct = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 100;

/** The shape, as one French sentence or null — the client mirror of
 *  `pms_cancellation_tiers_ok` and the table's CHECKs (20261026120100). */
export function cancellationPolicyProblem(p: Omit<CancellationPolicy, 'verifiedAt'>): string | null {
  if (!/^[A-Z0-9_]{1,30}$/.test(p.code)) return 'Code : lettres majuscules, chiffres et _ (30 au plus).';
  if (p.labelFr.trim() === '') return 'Libellé requis.';
  if (!isPct(p.prepaymentPct)) return 'Arrhes : un pourcentage entier de 0 à 100.';
  if (p.prepaymentAt !== 'booking' && p.prepaymentAt !== 'checkin') return 'Arrhes : à la réservation ou à l’arrivée.';
  if (!isPct(p.noShowPct)) return 'Non-présentation : un pourcentage entier de 0 à 100.';
  if (p.tiers.length === 0) return 'Au moins un palier d’annulation.';
  for (const [i, t] of p.tiers.entries()) {
    if (!isPct(t.retainPct)) return `Palier ${i + 1} : un pourcentage entier de 0 à 100.`;
    const last = i === p.tiers.length - 1;
    if (last !== (t.moreThanDaysBefore === null)) {
      return 'Le dernier palier va jusqu’à l’arrivée, et lui seul.';
    }
    if (t.moreThanDaysBefore !== null) {
      if (!Number.isInteger(t.moreThanDaysBefore) || t.moreThanDaysBefore < 1 || t.moreThanDaysBefore > 365) {
        return `Palier ${i + 1} : un nombre de jours de 1 à 365.`;
      }
      const prev = p.tiers[i - 1]?.moreThanDaysBefore;
      if (prev != null && t.moreThanDaysBefore >= prev) return 'Les paliers vont du plus tôt au plus tard (jours décroissants).';
    }
  }
  return null;
}

/** The four numbers alone — the desk's relaxed terms (PB4), checked as
 *  `pms_relaxed_terms` checks them. */
export function termsRulesProblem(r: TermsRules): string | null {
  return cancellationPolicyProblem({ code: 'BUREAU', labelFr: 'Conditions particulières', ota: false, ...r });
}

/** The deposit the terms ask AT BOOKING on the room nights (board in, extras and
 *  taxe de séjour out — the A5 base): `pms_reservation_apply`'s default (PB4) and
 *  the direct door's arithmetic. 0 when the prepayment is taken at check-in. */
export function bookingDeposit(rules: Pick<TermsRules, 'prepaymentPct' | 'prepaymentAt'>, roomNightsCents: Cents): Cents {
  return rules.prepaymentAt === 'booking' ? Math.round((roomNightsCents * rules.prepaymentPct) / 100) : 0;
}

// ── Dates ────────────────────────────────────────────────────────────────────

const addDays = (d: IsoDate, n: number): IsoDate => {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};

const daysBetween = (from: IsoDate, to: IsoDate): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

const retain = (totalCents: Cents, pct: number): Cents => Math.round((totalCents * pct) / 100);

/** The tier that applies to a cancellation made on `on` for an arrival on `arrival`. */
export function tierOn(policy: Pick<CancellationPolicy, 'tiers'>, arrival: IsoDate, on: IsoDate): CancellationTier {
  const before = daysBetween(on, arrival);
  return policy.tiers.find((t) => t.moreThanDaysBefore === null || before > t.moreThanDaysBefore) ?? policy.tiers.at(-1)!;
}

/** What a cancellation made on `on` retains, in cents. */
export function cancellationFee(
  policy: Pick<CancellationPolicy, 'tiers'>,
  arrival: IsoDate,
  on: IsoDate,
  totalCents: Cents,
): Cents {
  return retain(totalCents, tierOn(policy, arrival, on).retainPct);
}

export interface SchedulePeriod {
  /** First day this rate applies — null = from the booking. Inclusive. */
  from: IsoDate | null;
  /** Last day this rate applies. Inclusive; the arrival for the last tier. */
  to: IsoDate;
  retainPct: number;
  retainCents: Cents;
}

export interface CancellationSchedule {
  prepayment: { pct: number; cents: Cents; at: PrepaymentAt };
  /** The periods still to come, in order. A period that ended before `bookedOn`
   *  is dropped: « arrival inside the threshold, the before line is not read ». */
  periods: SchedulePeriod[];
  noShow: { pct: number; cents: Cents };
}

/** The policy applied to one stay: dates and amounts. */
export function cancellationSchedule(
  policy: TermsRules,
  stay: { arrival: IsoDate; totalCents: Cents; bookedOn?: IsoDate },
): CancellationSchedule {
  const periods: SchedulePeriod[] = [];
  let from: IsoDate | null = null;
  for (const t of policy.tiers) {
    // Strict « more than N days »: the last day of the tier is D−(N+1).
    const to = t.moreThanDaysBefore === null ? stay.arrival : addDays(stay.arrival, -(t.moreThanDaysBefore + 1));
    periods.push({ from, to, retainPct: t.retainPct, retainCents: retain(stay.totalCents, t.retainPct) });
    from = addDays(to, 1);
  }
  const booked = stay.bookedOn;
  const live =
    booked === undefined
      ? periods
      : periods
          .filter((p) => p.to >= booked)
          .map((p, i) => (i === 0 ? { ...p, from: null } : p));
  return {
    prepayment: { pct: policy.prepaymentPct, cents: retain(stay.totalCents, policy.prepaymentPct), at: policy.prepaymentAt },
    periods: live,
    noShow: { pct: policy.noShowPct, cents: retain(stay.totalCents, policy.noShowPct) },
  };
}

// ── The text (one renderer, FR / EN) ─────────────────────────────────────────

export type TermsLang = 'fr' | 'en' | 'es';
export const TERMS_LANGS: readonly TermsLang[] = ['fr', 'en', 'es'];

export interface RenderedTerms {
  /** « Arrhes » / « Prepayment ». */
  prepayment: string;
  /** One line per tier (the general rule) or per period (a sample stay). */
  cancellation: string[];
  noShow: string;
  /** What « montant total » covers. */
  basis: string;
}

const FR_DAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const FR_MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const EN_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const ES_DAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const ES_MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** « samedi 28 novembre » · « Saturday 28 November » · « sábado 28 de noviembre ». */
export function termsDate(d: IsoDate, lang: TermsLang): string {
  const t = new Date(`${d}T00:00:00Z`);
  if (lang === 'es') return `${ES_DAYS[t.getUTCDay()]} ${t.getUTCDate()} de ${ES_MONTHS[t.getUTCMonth()]}`;
  const [days, months] = lang === 'fr' ? [FR_DAYS, FR_MONTHS] : [EN_DAYS, EN_MONTHS];
  return `${days[t.getUTCDay()]} ${t.getUTCDate()} ${months[t.getUTCMonth()]}`;
}

/** « 62,46 € » · « €62.46 » · « 62,46 € ». */
export function termsAmount(cents: Cents, lang: TermsLang): string {
  const n = (cents / 100).toFixed(2);
  if (lang === 'en') return `€${n}`;
  const [i, f] = n.split('.');
  return `${i!.replace(/\B(?=(\d{3})+(?!\d))/g, lang === 'es' ? '.' : ' ')},${f} €`;
}

const pct = (n: number, lang: TermsLang) => (lang === 'en' ? `${n}%` : `${n} %`);

/** The sentences, per language. FR follows the SiteMinder framing, EN the
 *  extranets' (« Prepayment · Cancellation · No-show »), ES the site's own. */
interface Phrases {
  basis: string;
  noPrepayment: string;
  prepayment: (p: string, checkin: boolean) => string;
  anyTime: (r: string) => string;
  moreThan: (days: number, r: string) => string;
  orLess: (days: number, r: string) => string;
  noShow: (r: string) => string;
  stayPrepayment: (p: string, amount: string, checkin: boolean) => string;
  until: (date: string, r: string) => string;
  from: (date: string, r: string) => string;
  stayAnyTime: (r: string) => string;
  stayNoShow: (r: string) => string;
}

const PHRASES: Record<TermsLang, Phrases> = {
  fr: {
    basis: 'Le montant total comprend le séjour, les repas et les extras, hors taxe de séjour.',
    noPrepayment: 'Aucune arrhe n’est demandée.',
    prepayment: (p, c) => `Des arrhes de ${p} du montant total sont demandées ${c ? 'à l’arrivée' : 'à la réservation'}.`,
    anyTime: (r) => `En cas d’annulation à tout moment après la réservation, ${r} du montant total seront facturés.`,
    moreThan: (d, r) => `Si vous annulez plus de ${d} jours avant votre arrivée, ${r} du montant total seront retenus.`,
    orLess: (d, r) => `Si vous annulez ${d} jours ou moins avant votre arrivée, ${r} du montant total seront retenus.`,
    noShow: (r) => `En cas de non-présentation, ${r} du montant total seront facturés.`,
    stayPrepayment: (p, a, c) => `Arrhes de ${p} (${a}) ${c ? 'prélevées à l’arrivée' : 'à la réservation'}.`,
    until: (d, r) => `Annulation jusqu’au ${d} inclus : ${r}.`,
    from: (d, r) => `Annulation à partir du ${d} : ${r}.`,
    stayAnyTime: (r) => `Annulation à tout moment : ${r}.`,
    stayNoShow: (r) => `Non-présentation : ${r}.`,
  },
  en: {
    basis: 'The total amount covers the stay, meals and extras, excluding the tourist tax.',
    noPrepayment: 'No prepayment is required.',
    prepayment: (p, c) => `Prepayment: ${p} of the total amount is charged ${c ? 'at check-in' : 'at booking'}.`,
    anyTime: (r) => `Cancellation: if cancelled at any time after booking, ${r} of the total amount will be charged.`,
    moreThan: (d, r) => `If cancelled more than ${d} days before arrival, ${r} of the total amount will be charged.`,
    orLess: (d, r) => `If cancelled ${d} days or less before arrival, ${r} of the total amount will be charged.`,
    noShow: (r) => `No-show: ${r} of the total amount will be charged.`,
    stayPrepayment: (p, a, c) => `Prepayment of ${p} (${a}) ${c ? 'charged at check-in' : 'at booking'}.`,
    until: (d, r) => `Cancellation until ${d} inclusive: ${r}.`,
    from: (d, r) => `Cancellation from ${d}: ${r}.`,
    stayAnyTime: (r) => `Cancellation at any time: ${r}.`,
    stayNoShow: (r) => `No-show: ${r}.`,
  },
  es: {
    basis: 'El importe total incluye la estancia, las comidas y los extras, sin la tasa turística.',
    noPrepayment: 'No se solicita ningún anticipo.',
    prepayment: (p, c) => `Se solicita un anticipo del ${p} del importe total ${c ? 'a la llegada' : 'al reservar'}.`,
    anyTime: (r) => `En caso de cancelación en cualquier momento después de la reserva, se facturará el ${r} del importe total.`,
    moreThan: (d, r) => `Si cancela más de ${d} días antes de su llegada, se retendrá el ${r} del importe total.`,
    orLess: (d, r) => `Si cancela ${d} días o menos antes de su llegada, se retendrá el ${r} del importe total.`,
    noShow: (r) => `En caso de no presentarse, se facturará el ${r} del importe total.`,
    stayPrepayment: (p, a, c) => `Anticipo del ${p} (${a}) ${c ? 'cobrado a la llegada' : 'al reservar'}.`,
    until: (d, r) => `Cancelación hasta el ${d} inclusive: ${r}.`,
    from: (d, r) => `Cancelación a partir del ${d}: ${r}.`,
    stayAnyTime: (r) => `Cancelación en cualquier momento: ${r}.`,
    stayNoShow: (r) => `No presentación: ${r}.`,
  },
};

/**
 * The terms as sentences. Without a stay: the general rule, as the settings page
 * and the website's terms page show it. With a stay: its dates and amounts, as
 * the script, the read-back, the confirmation and the checkout say them.
 */
export function renderTerms(
  policy: TermsRules,
  lang: TermsLang,
  stay?: { arrival: IsoDate; totalCents: Cents; bookedOn?: IsoDate },
): RenderedTerms {
  const w = PHRASES[lang];
  const basis = w.basis;

  if (stay === undefined) {
    const prepayment =
      policy.prepaymentPct === 0 ? w.noPrepayment : w.prepayment(pct(policy.prepaymentPct, lang), policy.prepaymentAt === 'checkin');
    const only = policy.tiers.length === 1;
    const cancellation = policy.tiers.map((t, i) => {
      const r = pct(t.retainPct, lang);
      if (only) return w.anyTime(r);
      if (t.moreThanDaysBefore !== null) return w.moreThan(t.moreThanDaysBefore, r);
      return w.orLess(policy.tiers[i - 1]!.moreThanDaysBefore!, r);
    });
    return { prepayment, cancellation, noShow: w.noShow(pct(policy.noShowPct, lang)), basis };
  }

  const s = cancellationSchedule(policy, stay);
  const amt = (c: Cents) => termsAmount(c, lang);
  const prepayment =
    s.prepayment.pct === 0
      ? w.noPrepayment
      : w.stayPrepayment(pct(s.prepayment.pct, lang), amt(s.prepayment.cents), s.prepayment.at === 'checkin');
  const cancellation = s.periods.map((p, i) => {
    const r = `${pct(p.retainPct, lang)} (${amt(p.retainCents)})`;
    if (i < s.periods.length - 1) return w.until(termsDate(p.to, lang), r);
    return p.from === null ? w.stayAnyTime(r) : w.from(termsDate(p.from, lang), r);
  });
  return { prepayment, cancellation, noShow: w.stayNoShow(`${pct(s.noShow.pct, lang)} (${amt(s.noShow.cents)})`), basis };
}

/** The same terms in every language the website speaks — what the API sends,
 *  so the site displays the renderer's sentences rather than its own. */
export type TermsText = Record<TermsLang, RenderedTerms>;

export function renderTermsText(
  policy: TermsRules,
  stay?: { arrival: IsoDate; totalCents: Cents; bookedOn?: IsoDate },
): TermsText {
  return { fr: renderTerms(policy, 'fr', stay), en: renderTerms(policy, 'en', stay), es: renderTerms(policy, 'es', stay) };
}

/** The settings page's warning for an Expedia plan whose policy takes the
 *  prepayment at booking (P14): Expedia's virtual card activates only at check-in. */
export function expediaPrepaymentWarning(channelCode: string | null, policy: Pick<CancellationPolicy, 'prepaymentAt' | 'prepaymentPct'>): boolean {
  return channelCode === 'EXPEDIA' && policy.prepaymentPct > 0 && policy.prepaymentAt === 'booking';
}
